import { FilePlus2, FileSpreadsheet, LoaderCircle } from "lucide-react";
import { Link } from "react-router";
import { useNavigate } from "react-router";
import { useState } from "react";
import { createBlankWorkbook, getWorkbook } from "@/lib/api/endpoints";
import { InlineArrow } from "@/components/icons/InlineArrow";

export function NewWorkbookActions() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreateBlank() {
    if (creating) return;
    setCreating(true);
    setError(null);

    try {
      const workbook = await createBlankWorkbook({});
      // The standalone /edit page is gone — sheet editing now lives in the
      // mapper's editable Source tab. Resolve the first sheet name and open the
      // mapper there. If we can't resolve a sheet, fall back to the workbook
      // overview where the user can pick a sheet.
      let target = `/workbooks/${workbook.id}`;
      try {
        const full = await getWorkbook(workbook.id);
        const firstSheet = full.workbook.sheets?.[0];
        if (firstSheet) {
          target = `/workbooks/${workbook.id}/sheets/${encodeURIComponent(
            firstSheet.name,
          )}?tab=source`;
        }
      } catch {
        /* fall back to the workbook overview */
      }
      navigate(target);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not create workbook",
      );
      setCreating(false);
    }
  }

  return (
    <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
      <div className="rounded-xl border border-zinc-900 bg-zinc-900 p-6 text-white transition dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-950">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-semibold">New blank workbook</div>
            <p className="mt-1 text-sm text-zinc-300 dark:text-zinc-600">
              Start with an editable sheet, then import CSV or enter cells by hand.
            </p>
          </div>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/10 text-white dark:bg-zinc-950/10 dark:text-zinc-950">
            <FilePlus2 size={20} />
          </span>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleCreateBlank}
            disabled={creating}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-white px-4 text-sm font-medium text-zinc-950 transition hover:bg-zinc-100 disabled:cursor-wait disabled:opacity-75 dark:bg-zinc-950 dark:text-white dark:hover:bg-zinc-800"
          >
            {creating ? (
              <LoaderCircle size={16} className="animate-spin" />
            ) : (
              <FilePlus2 size={16} />
            )}
            {creating ? "Creating" : "Create workbook"}
          </button>
          {error && (
            <span className="text-xs font-medium text-red-200 dark:text-red-700">
              {error}
            </span>
          )}
        </div>
      </div>

      <Link
        to="/upload"
        className="group flex flex-col justify-between rounded-xl border border-zinc-200 bg-white p-6 transition hover:border-zinc-300 hover:shadow-sm dark:border-border dark:bg-surface dark:hover:border-zinc-600"
      >
        <div>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-zinc-100 text-zinc-700 dark:bg-surface-2 dark:text-zinc-200">
            <FileSpreadsheet size={18} />
          </span>
          <div className="mt-4 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            Upload Excel
          </div>
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            Use an existing supplier .xlsx workbook.
          </p>
        </div>
        <span className="mt-4 text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Upload <InlineArrow className="transition group-hover:translate-x-0.5" />
        </span>
      </Link>
    </div>
  );
}
